import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from 'react-slick';
import LoaderHelper from '../../../customComponents/Loading/LoaderHelper';
import AuthService from '../../../api/services/AuthService';
import { alertErrorMessage, alertSuccessMessage } from '../../../customComponents/CustomAlertMessage';
import { ApiConfig } from '../../../api/apiConfig/apiConfig';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { $ } from 'react-jquery-plugin';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';
import { usePlatformStatus } from '../../../context/PlatformStatusProvider';
import toast from 'react-hot-toast';
import ReferralHistorySection from '../../../customComponents/ReferralHistorySection';
import ReferralDetailsSection from '../../../customComponents/ReferralDetailsSection';
import './earningPayoutModal.css';
import {
  getSubscribeWalletLabel,
  isStakingWallet,
  STAKING_WALLET_COPY,
} from '../../../utils/walletTypes';

/** Replaces browser `confirm` with a toast that has Yes/No actions (same stack as alertSuccessMessage). */
function confirmCancelEarningPlan() {
  return new Promise((resolve) => {
    toast.custom(
      (t) => (
        <div
          style={{
            background: '#2b313c',
            color: '#e0e0e0',
            padding: '16px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
            maxWidth: 400,
            fontSize: 14,
            lineHeight: 1.45,
          }}
        >
          <p style={{ margin: '0 0 14px' }}>
            Cancel this active earning plan? A cancellation fee may apply; the remaining balance will be
            refunded per platform rules.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-light"
              onClick={() => {
                toast.dismiss(t.id);
                resolve(false);
              }}
            >
              Keep plan
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => {
                toast.dismiss(t.id);
                resolve(true);
              }}
            >
              Yes, cancel
            </button>
          </div>
        </div>
      ),
      { duration: Infinity, position: 'top-center' }
    );
  });
}

function Earning() {
  const { getStatus } = usePlatformStatus();
  const isStakingDisabled = !getStatus('staking').enabled;
  // Ref for component mount state
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preferredWalletType = searchParams.get('walletType') || '';

  // State management
  const [packageList, setPackageList] = useState([]);
  const [search, setSearch] = useState("");
  const [packageDetails, setPackageDetails] = useState({});
  const [walletType, setWalletType] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState("");
  const [walletBalance, setWalletBalance] = useState(0);
  const [subscriptionAmount, setSubscriptionAmount] = useState("");
  const [searchPackage] = useState("");

  const [subscribedActivePackages, setSubscribedActivePackages] = useState([]);
  const [subscribedCompletedPackage, setSubscribedCompletedPackage] = useState([]);
  const [portfolio, setPortfolio] = useState([]); // Array of currency-wise portfolio
  const [portfolioSummary, setPortfolioSummary] = useState({}); // Summary in USDT

  // Team staking (level) performance
  const [teamPerformanceMembers, setTeamPerformanceMembers] = useState([]);
  const [teamPerformanceSummary, setTeamPerformanceSummary] = useState({});
  const [teamPerformanceLoading, setTeamPerformanceLoading] = useState(false);

  // Brokerage commissions
  const [brokerageList, setBrokerageList] = useState([]);
  const [brokerageSummary, setBrokerageSummary] = useState({});
  const [brokerageLoading, setBrokerageLoading] = useState(false);

  // Loading states
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Pagination states
  const [skipActive] = useState(0);
  const [skipCompleted] = useState(0);
  const limit = 10;

  // Agreement checkbox
  const [agreeTerms, setAgreeTerms] = useState(false);

  // Expand row states for mobile view
  const [showActiveExpandedRows, setShowActiveExpandedRows] = useState({});
  const [showCompletedExpandedRows, setShowCompletedExpandedRows] = useState({});

  const [cancellingStakingId, setCancellingStakingId] = useState(null);

  // Per-day payout history modal (Earning Balance History → View)
  const [payoutModalStaking, setPayoutModalStaking] = useState(null);
  const [payoutHistoryList, setPayoutHistoryList] = useState([]);
  const [payoutHistoryLoading, setPayoutHistoryLoading] = useState(false);
  const [payoutHistoryError, setPayoutHistoryError] = useState("");

  // Store all packages for selected currency (for duration selection)
  const [selectedCurrencyPackages, setSelectedCurrencyPackages] = useState([]);

  const token = localStorage.getItem("token");

  // Utility functions
  const formatToNineDecimals = useCallback((data) => {
    if (typeof data === "number" && !isNaN(data)) {
      return parseFloat(data.toFixed(9));
    }
    return 0;
  }, []);

  const formatNumber = useCallback((num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return "0.00";
    return parseFloat(num).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }, []);

  const parsePayoutAmount = useCallback((amount) => {
    if (amount == null) return 0;
    if (typeof amount === "object" && amount.$numberDecimal != null) {
      const n = parseFloat(amount.$numberDecimal);
      return Number.isFinite(n) ? n : 0;
    }
    const n = parseFloat(amount);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const readStakingMoney = useCallback((item, ...keys) => {
    for (const k of keys) {
      const v = item?.[k];
      if (v == null) continue;
      if (typeof v === "object" && v.$numberDecimal != null) {
        const n = parseFloat(v.$numberDecimal);
        if (Number.isFinite(n)) return n;
      } else {
        const n = parseFloat(v);
        if (Number.isFinite(n)) return n;
      }
    }
    return null;
  }, []);

  const isCancelledSubscriptionStatus = useCallback((status) => {
    const s = String(status || "").toUpperCase();
    return s === "CANCELLED" || s === "CANCELED" || s.includes("CANCEL");
  }, []);

  const openPayoutHistory = useCallback(async (item) => {
    const userId = localStorage.getItem("userId");
    const stakingId = item?._id;
    if (!token) {
      navigate("/login");
      return;
    }
    if (!userId || !stakingId) {
      alertErrorMessage("Unable to open payout history. Please try again.");
      return;
    }
    setPayoutModalStaking(item);
    setPayoutHistoryList([]);
    setPayoutHistoryError("");
    setPayoutHistoryLoading(true);
    $("#payout_history_modal").modal("show");
    try {
      const result = await AuthService.getPerDayPayoutHistory(userId, stakingId);
      if (result?.success) {
        const raw = result?.data?.payout_history || [];
        const sorted = [...raw].sort((a, b) => (Number(a?.day) || 0) - (Number(b?.day) || 0));
        setPayoutHistoryList(sorted);
      } else {
        setPayoutHistoryError(result?.message || "Could not load payout history.");
      }
    } catch (error) {
      console.error("getPerDayPayoutHistory:", error);
      setPayoutHistoryError(error?.message || "Could not load payout history.");
    } finally {
      setPayoutHistoryLoading(false);
    }
  }, [token, navigate]);

  // Filtered subscribed packages based on search
  const filteredActivePackages = useMemo(() => {
    if (!search.trim()) return subscribedActivePackages;
    const searchLower = search.toLowerCase().trim();
    return subscribedActivePackages.filter(item =>
      item?.currency?.toLowerCase().includes(searchLower) ||
      item?.wallet_type?.toLowerCase().includes(searchLower) ||
      item?.status?.toLowerCase().includes(searchLower)
    );
  }, [subscribedActivePackages, search]);

  const filteredCompletedPackages = useMemo(() => {
    if (!search.trim()) return subscribedCompletedPackage;
    const searchLower = search.toLowerCase().trim();
    return subscribedCompletedPackage.filter(item =>
      item?.currency?.toLowerCase().includes(searchLower) ||
      item?.credited_wallet_type?.toLowerCase().includes(searchLower) ||
      item?.status?.toLowerCase().includes(searchLower)
    );
  }, [subscribedCompletedPackage, search]);

  const parseStakingAmount = useCallback((value) => {
    if (value == null) return 0;
    if (typeof value === "object" && value.$numberDecimal != null) {
      const parsed = parseFloat(value.$numberDecimal);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const earningAssetsPortfolio = useMemo(() => {
    const portfolioMetaByCurrency = (portfolio || []).reduce((acc, item) => {
      if (item?.currency) acc[item.currency] = item;
      return acc;
    }, {});

    const packages = [...subscribedActivePackages, ...subscribedCompletedPackage].filter(
      (item) => !isCancelledSubscriptionStatus(item?.status)
    );

    if (packages.length > 0) {
      const byCurrency = {};

      packages.forEach((item) => {
        const currency = item?.currency;
        if (!currency) return;

        if (!byCurrency[currency]) {
          const meta = portfolioMetaByCurrency[currency] || {};
          byCurrency[currency] = {
            _id: meta._id || currency,
            currency,
            currency_fullname: meta.currency_fullname || item?.currency_fullname,
            icon_path: meta.icon_path || item?.icon_path,
            invested_amount: 0,
            expected_return: 0,
            running_investment: 0,
          };
        }

        const invested = parseStakingAmount(item?.invested_amount);
        const expected = parseStakingAmount(item?.expected_return);
        const running = item?.status === "ACTIVE"
          ? parseStakingAmount(item?.running_investment ?? item?.remaining ?? item?.invested_amount)
          : 0;

        byCurrency[currency].invested_amount += invested;
        byCurrency[currency].expected_return += expected;
        byCurrency[currency].running_investment += running;
      });

      return Object.values(byCurrency).filter(
        (item) => item.invested_amount > 0 || item.expected_return > 0 || item.running_investment > 0
      );
    }

    return (portfolio || [])
      .filter((item) => {
        if (isCancelledSubscriptionStatus(item?.status) || isCancelledSubscriptionStatus(item?.staking_status)) {
          return false;
        }
        if (item?.is_cancelled || item?.cancelled) return false;

        const cancelledAmount = readStakingMoney(
          item,
          "cancelled_investment",
          "cancel_staking",
          "cancelled_staking",
          "cancel_staking_amount",
          "cancelled_amount"
        ) ?? 0;
        const invested = readStakingMoney(item, "invested_amount", "total_invested") ?? 0;
        const running = readStakingMoney(item, "running_investment", "running") ?? 0;
        const expected = readStakingMoney(item, "expected_return") ?? 0;

        if (cancelledAmount > 0 && invested <= cancelledAmount && running === 0 && expected === 0) {
          return false;
        }

        return invested > 0 || running > 0 || expected > 0;
      })
      .map((item) => {
        const cancelledAmount = readStakingMoney(
          item,
          "cancelled_investment",
          "cancel_staking",
          "cancelled_staking",
          "cancel_staking_amount",
          "cancelled_amount"
        );

        if (cancelledAmount == null || cancelledAmount <= 0) return item;

        return {
          ...item,
          invested_amount: Math.max(0, (readStakingMoney(item, "invested_amount", "total_invested") ?? 0) - cancelledAmount),
          expected_return: Math.max(0, (readStakingMoney(item, "expected_return") ?? 0) - cancelledAmount),
          running_investment: Math.max(0, (readStakingMoney(item, "running_investment", "running") ?? 0) - cancelledAmount),
        };
      });
  }, [
    portfolio,
    subscribedActivePackages,
    subscribedCompletedPackage,
    isCancelledSubscriptionStatus,
    parseStakingAmount,
    readStakingMoney,
  ]);

  // API calls with proper error handling
  const getPackageList = useCallback(async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.packageList();
      if (!isMountedRef.current) return;

      if (result?.success) {
        setPackageList(result?.data || []);
      }
    } catch (error) {
      console.error("Error fetching package list:", error);
    } finally {
      if (isMountedRef.current) {
        LoaderHelper.loaderStatus(false);
      }
    }
  }, []);

  const earningPortfolio = useCallback(async () => {
    if (!token) return;
    try {
      const result = await AuthService.earningPortfolio();
      if (!isMountedRef.current) return;

      if (result?.success) {
        setPortfolio(result?.data || []);
      }
    } catch (error) {
      console.error("Error fetching earning portfolio:", error);
    }
  }, [token]);

  const earningPortfolioSummary = useCallback(async () => {
    if (!token) return;
    try {
      const result = await AuthService.earningPortfolioSummary();
      if (!isMountedRef.current) return;

      if (result?.success) {
        setPortfolioSummary(result?.data || {});
      }
    } catch (error) {
      console.error("Error fetching earning portfolio summary:", error);
    }
  }, [token]);

  const getTeamStakingPerformance = useCallback(async () => {
    if (!token) return;
    try {
      setTeamPerformanceLoading(true);
      const result = await AuthService.getTeamStakingPerformance();
      if (!isMountedRef.current) return;

      if (result?.success) {
        setTeamPerformanceMembers(result?.data?.members || []);
        setTeamPerformanceSummary(result?.data?.summary || {});
      }
    } catch (error) {
      console.error("Error fetching team staking performance:", error);
    } finally {
      if (isMountedRef.current) setTeamPerformanceLoading(false);
    }
  }, [token]);

  const getBrokerageCommissions = useCallback(async () => {
    if (!token) return;
    try {
      setBrokerageLoading(true);
      const result = await AuthService.getBrokerageCommissions(1, 100);
      if (!isMountedRef.current) return;

      if (result?.success) {
        setBrokerageList(Array.isArray(result?.data) ? result.data : []);
        setBrokerageSummary(result?.summary || {});
      }
    } catch (error) {
      console.error("Error fetching brokerage commissions:", error);
    } finally {
      if (isMountedRef.current) setBrokerageLoading(false);
    }
  }, [token]);

  const subscribedPackageList = useCallback(async (skip = 0, fetchLimit = 500) => {
    if (!token) return;
    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.subscribedPackageList(skip, fetchLimit);
      if (!isMountedRef.current) return;

      if (result?.success) {
        const allData = result?.data || [];
        const completedPackage = allData.filter(
          (item) => item?.status === "COMPLETED" && !isCancelledSubscriptionStatus(item?.status)
        );
        const activePackage = allData.filter(
          (item) => item?.status === "ACTIVE" && !isCancelledSubscriptionStatus(item?.status)
        );

        setSubscribedActivePackages(activePackage);
        setSubscribedCompletedPackage(completedPackage);
      }
    } catch (error) {
      console.error("Error fetching subscribed packages:", error);
    } finally {
      if (isMountedRef.current) {
        LoaderHelper.loaderStatus(false);
      }
    }
  }, [token, isCancelledSubscriptionStatus]);

  const handleCancelSubscription = useCallback(
    async (item) => {
      const stakingId = item?._id;
      if (!token) {
        navigate("/login");
        return;
      }
      if (!stakingId) {
        alertErrorMessage("Unable to cancel. Please try again.");
        return;
      }
      const confirmed = await confirmCancelEarningPlan();
      if (!confirmed) {
        return;
      }
      try {
        setCancellingStakingId(stakingId);
        LoaderHelper.loaderStatus(true);
        const result = await AuthService.cancelEarningSubscription(stakingId);
        if (!isMountedRef.current) return;

        if (result?.success) {
          const d = result?.data || {};
          let msg = result?.message || "Plan cancelled successfully";
          if (d.refundAmount != null && item?.currency) {
            msg += ` Refund: ${formatNumber(parseFloat(d.refundAmount))} ${item.currency}.`;
          }
          alertSuccessMessage(msg);
          subscribedPackageList();
          earningPortfolio();
          earningPortfolioSummary();
        } else {
          alertErrorMessage(result?.message || "Could not cancel this plan.");
        }
      } catch (error) {
        console.error("cancelEarningSubscription:", error);
        alertErrorMessage(error?.message || "Could not cancel this plan.");
      } finally {
        if (isMountedRef.current) {
          setCancellingStakingId(null);
          LoaderHelper.loaderStatus(false);
        }
      }
    },
    [
      token,
      navigate,
      formatNumber,
      subscribedPackageList,
      earningPortfolio,
      earningPortfolioSummary,
    ]
  );

  const getWalletType = useCallback(async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.availableWalletTypes();
      if (!isMountedRef.current) return;

      if (result?.success) {
        const types = result?.data || [];
        setWalletType(types);
        if (preferredWalletType && types.includes(preferredWalletType)) {
          setSelectedWallet(preferredWalletType);
        } else if (types.length > 0) {
          setSelectedWallet(types[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching wallet types:", error);
    } finally {
      if (isMountedRef.current) {
        LoaderHelper.loaderStatus(false);
      }
    }
  }, [preferredWalletType]);

  const getWalletBalance = useCallback(async () => {
    if (!packageDetails?.currency_id || !selectedWallet) return;

    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.getWalletBalance(packageDetails.currency_id, selectedWallet);
      if (!isMountedRef.current) return;

      if (result?.success) {
        setWalletBalance(formatToNineDecimals(result?.data?.balance || 0));
      } else {
        setWalletBalance(0);
      }
    } catch (error) {
      console.error("Error fetching wallet balance:", error);
      if (isMountedRef.current) {
        setWalletBalance(0);
      }
    } finally {
      if (isMountedRef.current) {
        LoaderHelper.loaderStatus(false);
      }
    }
  }, [packageDetails?.currency_id, selectedWallet, formatToNineDecimals]);

  // Subscription handlers
  const resetInput = useCallback(() => {
    setSubscriptionAmount("");
    setPackageDetails({});
    setAgreeTerms(false);
    setSelectedCurrencyPackages([]);
  }, []);

  const subscribeEarningPackage = useCallback(async () => {
    if (isStakingDisabled) return;
    if (!packageDetails?._id) {
      alertErrorMessage("Please select a package");
      return;
    }
    if (!subscriptionAmount || parseFloat(subscriptionAmount) <= 0) {
      alertErrorMessage("Please enter a valid subscription amount");
      return;
    }
    if (!selectedWallet) {
      alertErrorMessage("Please select a wallet");
      return;
    }
    if (parseFloat(subscriptionAmount) > walletBalance) {
      alertErrorMessage("Insufficient balance in selected wallet");
      return;
    }
    if (packageDetails?.min_amount && parseFloat(subscriptionAmount) < packageDetails.min_amount) {
      alertErrorMessage(`Minimum subscription amount is ${packageDetails.min_amount} ${packageDetails.currency}`);
      return;
    }
    if (packageDetails?.max_amount && parseFloat(subscriptionAmount) > packageDetails.max_amount) {
      alertErrorMessage(`Maximum subscription amount is ${packageDetails.max_amount} ${packageDetails.currency}`);
      return;
    }
    if (!agreeTerms) {
      alertErrorMessage("Please agree to the Earn Service Agreement");
      return;
    }

    try {
      setIsSubscribing(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.subscribeEarningPackage(
        packageDetails._id,
        parseFloat(subscriptionAmount),
        selectedWallet
      );

      if (!isMountedRef.current) return;

      if (result?.success) {
        resetInput();
        $("#more_details").modal('hide');
        $("#package_details").modal('hide');
        alertSuccessMessage(result?.message || "Subscription successful!");
        // Refresh data
        subscribedPackageList();
        earningPortfolio();
        earningPortfolioSummary();
      } else {
        alertErrorMessage(result?.message || "Subscription failed");
      }
    } catch (error) {
      console.error("Error subscribing to package:", error);
      alertErrorMessage(error?.message || "An error occurred during subscription");
    } finally {
      if (isMountedRef.current) {
        setIsSubscribing(false);
        LoaderHelper.loaderStatus(false);
      }
    }
  }, [isStakingDisabled, packageDetails, subscriptionAmount, selectedWallet, walletBalance, agreeTerms, resetInput, subscribedPackageList, earningPortfolio, earningPortfolioSummary]);

  // UI handlers
  const showPackageDetails = useCallback((data, allCurrencyPackages = null) => {
    if (!token) {
      navigate('/login');
      return;
    }
    resetInput();
    setPackageDetails(data);

    // Find all packages for this currency from packageList
    if (allCurrencyPackages) {
      // Sort by duration_days ascending
      const sortedPackages = [...allCurrencyPackages].sort((a, b) => a.duration_days - b.duration_days);
      setSelectedCurrencyPackages(sortedPackages);
    } else {
      // Find from packageList by currency_id
      const currencyGroup = packageList.find(group =>
        Array.isArray(group) && group.some(pkg => pkg.currency_id === data.currency_id)
      );
      if (currencyGroup) {
        const sortedPackages = [...currencyGroup].sort((a, b) => a.duration_days - b.duration_days);
        setSelectedCurrencyPackages(sortedPackages);
      } else {
        setSelectedCurrencyPackages([data]);
      }
    }

    $("#package_details").modal('show');
    getWalletType();
  }, [token, navigate, resetInput, getWalletType, packageList]);

  // Handle duration selection in modal
  const handleDurationSelect = useCallback((pkg) => {
    setPackageDetails(pkg);
    setSubscriptionAmount(""); // Reset amount when duration changes
  }, []);

  const handleMaxAmount = useCallback(() => {
    if (walletBalance > 0) {
      const maxAmount = packageDetails?.max_amount
        ? Math.min(walletBalance, packageDetails.max_amount)
        : walletBalance;
      setSubscriptionAmount(maxAmount.toString());
    }
  }, [walletBalance, packageDetails?.max_amount]);

  const handleSubscriptionAmountChange = useCallback((e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setSubscriptionAmount(value);
    }
  }, []);

  // Memoized filtered data
  const filteredPackageList = useMemo(() => {
    if (!packageList || !Array.isArray(packageList)) return [];
    return packageList.filter(group =>
      Array.isArray(group) && group.some(item =>
        item?.currency?.toLowerCase().includes(searchPackage.toLowerCase()) ||
        item?.currency_fullname?.toLowerCase().includes(searchPackage.toLowerCase())
      )
    );
  }, [packageList, searchPackage]);

  // Flatten packages for "All Products" table
  const allPackagesFlat = useMemo(() => {
    if (!packageList || !Array.isArray(packageList)) return [];
    return packageList.flat().filter(Boolean);
  }, [packageList]);

  // Group packages by currency for display
  const groupedPackages = useMemo(() => {
    const groups = {};
    allPackagesFlat.forEach(pkg => {
      const key = pkg.currency_id || pkg.currency;
      if (!groups[key]) {
        groups[key] = {
          currency: pkg.currency,
          currency_fullname: pkg.currency_fullname,
          icon_path: pkg.icon_path,
          packages: []
        };
      }
      groups[key].packages.push(pkg);
    });
    return Object.values(groups);
  }, [allPackagesFlat]);

  // Calculate returns based on return percentage for the duration
  // return_percentage is the TOTAL return for that specific duration (not annual)
  // Total Return = Amount * return_percentage / 100
  // Daily Return = Total Return / duration_days
  const totalReturns = useMemo(() => {
    if (!subscriptionAmount || !packageDetails?.return_percentage) return "0.00";
    const amount = parseFloat(subscriptionAmount) || 0;
    const returnPercentage = packageDetails.return_percentage || 0;
    // Total return = Amount * percentage / 100
    const total = amount * returnPercentage / 100;
    return formatToNineDecimals(total);
  }, [subscriptionAmount, packageDetails, formatToNineDecimals]);

  const dailyReturns = useMemo(() => {
    if (!subscriptionAmount || !packageDetails?.return_percentage || !packageDetails?.duration_days) return "0.00";
    const amount = parseFloat(subscriptionAmount) || 0;
    const returnPercentage = packageDetails.return_percentage || 0;
    const days = packageDetails.duration_days || 1;
    // Daily return = (Amount * percentage / 100) / days
    const daily = (amount * returnPercentage / 100) / days;
    return formatToNineDecimals(daily);
  }, [subscriptionAmount, packageDetails, formatToNineDecimals]);

  const totalReceivable = useMemo(() => {
    if (!subscriptionAmount) return "0.00";
    const amount = parseFloat(subscriptionAmount) || 0;
    const returns = parseFloat(totalReturns) || 0;
    return formatToNineDecimals(amount + returns);
  }, [subscriptionAmount, totalReturns, formatToNineDecimals]);

  // Slider settings
  const getSliderSettings = useCallback((packageNumber) => {
    const slidesToShow = Math.min(packageNumber, 3);
    return {
      dots: true,
      infinite: packageNumber > 3,
      speed: 600,
      slidesToShow: slidesToShow,
      slidesToScroll: 1,
      autoplay: false,
      autoplaySpeed: 3000,
      arrows: false,
      responsive: [
        {
          breakpoint: 1366,
          settings: {
            slidesToShow: Math.min(packageNumber, 2),
            dots: true,
            arrows: false,
          },
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 1,
            dots: true,
            arrows: false,
          },
        },
        {
          breakpoint: 600,
          settings: {
            slidesToShow: 1,
            dots: true,
            arrows: false,
          },
        },
      ],
    };
  }, []);

  // Effects
  useEffect(() => {
    isMountedRef.current = true;
    getPackageList();
    if (token) {
      earningPortfolio();
      earningPortfolioSummary();
      subscribedPackageList();
      getTeamStakingPerformance();
      getBrokerageCommissions();
    }

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedWallet && packageDetails?.currency_id) {
      getWalletBalance();
    }
  }, [selectedWallet, packageDetails?.currency_id, getWalletBalance]);

  useEffect(() => {
 window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Rewards & Yield – Zillion Exchange Earning Platform</title>
        <meta
          name="description"
          content="Access Zillion Exchange earning programs: rewards, staking and yield-based crypto solutions on a trusted platform."
        />
        <meta
          name="keywords"
          content="crypto yield, earn with crypto, Zillion Exchange earning, crypto rewards platform"
        />
      </Helmet>

      <section className='earning_outer_s'>
        <div className='earning_section_cate'>
          <div className='earning_right_tab'>
            <div className='toptabs_hd'>
              <div className='container'>
                <ul className="nav nav-tabs" id="myTab" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button className="nav-link active" id="home-tab" data-bs-toggle="tab" data-bs-target="#home" type="button" role="tab" aria-controls="home" aria-selected="true">Earning</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="profile-tab" data-bs-toggle="tab" data-bs-target="#profile" type="button" role="tab" aria-controls="profile" aria-selected="false">Earning Dashboard</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="level-performance-tab" data-bs-toggle="tab" data-bs-target="#levelPerformance" type="button" role="tab" aria-controls="levelPerformance" aria-selected="false">Staking Team Level Performance</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="referral-performance-tab" data-bs-toggle="tab" data-bs-target="#referralPerformance" type="button" role="tab" aria-controls="referralPerformance" aria-selected="false">Referral Performance Team</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="brokerage-commission-tab" data-bs-toggle="tab" data-bs-target="#brokerageCommission" type="button" role="tab" aria-controls="brokerageCommission" aria-selected="false">Brokerage Commission</button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="my-referrals-tab" data-bs-toggle="tab" data-bs-target="#myReferrals" type="button" role="tab" aria-controls="myReferrals" aria-selected="false">My Referrals</button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className='container'>
            <div className="tab-content" id="myTabContent">
              {/* Earning Tab */}
              <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
                <div className='exchange_earning_bnr'>
                  <div className='earningbnr_cnt'>
                    <h2>Zillion Exchange Earning</h2>
                    {/* <p>New user exclusive: Up to <span>600% APR</span></p> */}
                    {!token && (
                      <button className='signbtn' onClick={() => navigate('/register')}>Sign Up Now</button>
                    )}
                  </div>
                  <div className='earning_bnr'>
                    <img src="/images/earining_bnr_vector.png" alt="Exchange Earning" />
                  </div>
                </div>

                <div className='earning_list_block'>
                  {filteredPackageList?.length > 0 ? (
                    filteredPackageList.slice(0, 1).map((packages, groupIndex) => {
                      const groupSlidesCount = packages.length;
                      const groupSettings = getSliderSettings(groupSlidesCount);

                      return (
                        <div key={groupIndex} className="slider_group_wrapper">
                          <Slider {...groupSettings}>
                            {packages.map((item) => (
                              <div key={item._id} className="currency_list_b ">
                                <ul>
                                  <li className="currency_list_b_li">
                                    <div className="currency_bit">
                                      <div className="currency_bit_inner">
                                        <img
                                          src={ApiConfig?.baseImage + item?.icon_path}
                                          className="img-fluid"
                                          alt={item?.currency_fullname}
                                          onError={(e) => { e.target.src = '/images/default-coin.png'; }}
                                        />
                                        <h2>
                                          {item?.currency}
                                          <span>({item?.currency_fullname})</span>
                                        </h2>
                                      </div>
                                      <span className='newtag'>Trending<i className="ri-fire-line"></i></span>
                                    </div>
                                    <ul className='usd_detail_list'>
                                      <li><span>{item?.duration_days}</span> Days</li>
                                      <li className='pricevalue'><span>% {item?.return_type || ""}</span>{item?.returnPercenatgeShowtoUser != null && item?.returnPercenatgeShowtoUser !== "" ? Number(item.returnPercenatgeShowtoUser).toFixed(2) : "—"}</li>
                                    </ul>
                                    <div className="currency_bit_footer">
                                      <button className="subscribe_btn">
                                        {token ? (
                                          <a href="#/" onClick={(e) => { e.preventDefault(); showPackageDetails(item, packages); }}>Subscribe</a>
                                        ) : (
                                          <Link to="/login">Login</Link>
                                        )}
                                      </button>
                                    </div>
                                  </li>
                                </ul>
                              </div>
                            ))}
                          </Slider>
                        </div>
                      );
                    })
                  ) : (
                    <div className='d-flex justify-content-center'>
                      <div className="no_data_outer">
                        <div className="table-responsive">
                          <div className="no_data_vector">
                            <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                            <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* All Products Section */}
                  <div className='all_product_data'>
                    <h3>All Plans</h3>

                    {/* Desktop View */}
                    <div className='desktop_view'>  
                      <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Token</th>
                            <th>Est. APR</th>
                            <th>Duration</th>
                            <th className='action_td'>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedPackages.length > 0 ? (
                            groupedPackages.map((group, index) => {
                              const minApr = Math.min(...group.packages.map(p => Number(p.returnPercenatgeShowtoUser) || 0));
                              const maxApr = Math.max(...group.packages.map(p => Number(p.returnPercenatgeShowtoUser) || 0));
                              const minDays = Math.min(...group.packages.map(p => p.duration_days || 0));
                              const maxDays = Math.max(...group.packages.map(p => p.duration_days || 0));

                              return (
                                <tr key={group.currency + index}>
                                  <td>
                                    <div className="td_div">
                                      <img
                                        alt={group.currency}
                                        src={ApiConfig?.baseImage + group.icon_path}
                                        className="img-fluid icon_img coinimg me-2"
                                        onError={(e) => { e.target.src = '/images/default-coin.png'; }}
                                      />
                                      {group.currency}
                                    </div>
                                  </td>
                                  <td>{minApr.toFixed(2)}% ~ {maxApr.toFixed(2)}%</td>
                                  <td>{minDays}/{maxDays} days</td>
                                  <td className='action_td'>
                                    <span className="btn custom-btn subscribebtn">
                                      <button onClick={() => showPackageDetails(group.packages[0], group.packages)}>
                                        Subscribe
                                      </button>
                                    </span>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr className="no-data-row">
                              <td colSpan="4">
                                <div className="no-data-wrapper">
                                  <div className="no_data_vector">
                                    <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                                    <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                 
                  </div>
                     {/* Mobile View */}
                     <div className='mobile_view'>
                      <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Token</th>
                            <th>Est. APR</th>
                            <th className='action_td'>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedPackages.length > 0 ? (
                            groupedPackages.map((group, index) => {
                              const minApr = Math.min(...group.packages.map(p => Number(p.returnPercenatgeShowtoUser) || 0));
                              const maxApr = Math.max(...group.packages.map(p => Number(p.returnPercenatgeShowtoUser) || 0));
                              const minDays = Math.min(...group.packages.map(p => p.duration_days || 0));
                              const maxDays = Math.max(...group.packages.map(p => p.duration_days || 0));

                              return (
                                <tr key={group.currency + index} onClick={() => showPackageDetails(group.packages[0], group.packages)} style={{ cursor: 'pointer' }}>
                                  <td>
                                    <div className="td_div">
                                      {/* <span className="star_btn btn_icon">
                                        <i className="ri ri-star-line me-2"></i>
                                      </span> */}
                                      <img
                                        alt={group.currency}
                                        src={ApiConfig?.baseImage + group.icon_path}
                                        className="img-fluid icon_img coinimg me-2"
                                        onError={(e) => { e.target.src = '/images/default-coin.png'; }}
                                      />
                                      {group.currency}
                                    </div>
                                  </td>
                                  <td>{minApr.toFixed(2)}% ~ {maxApr.toFixed(2)}%</td>
                                  <td className='action_td'>
                                    <span className="btn custom-btn subscribebtn">
                                      <button onClick={() => showPackageDetails(group.packages[0], group.packages)}>
                                        Subscribe
                                      </button>
                                    </span>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr className="no-data-row">
                              <td colSpan="3">
                                <div className="no-data-wrapper">
                                  <div className="no_data_vector">
                                    <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                                    <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      </div>
                    </div>
                </div>
              </div>
            </div>

            {/* Earning Dashboard Tab */}
            <div className="tab-pane fade" id="profile" role="tabpanel" aria-labelledby="profile-tab">
              <div className='exchange_earning_bnr'>
                <div className='earningbnr_cnt'>
                  <h2>Zillion Exchange Earning Balance</h2>
                  <p>View your total earnings, rewards, and growth in one place.</p>
                  {!token && (
                    <button className='signbtn' onClick={() => navigate('/register')}>Sign Up Now</button>
                  )}
                </div>
                <div className='earning_bnr'>
                  <img src="/images/earning_balance_vector.png" alt="Exchange Earning" />
                </div>
              </div>

              <ul className='balance_list_s'>
                <li>
                  <div className='balance_cnt'>
                    <h4>{formatNumber(portfolioSummary?.total_invested || 0)} $</h4>
                    <p>Total Invested</p>
                  </div>
                  <div className='balance_vector'>
                    <img src="/images/wallet_coins_balance.svg" alt="Earning Balance" />
                  </div>
                </li>
                <li>
                  <div className='balance_cnt'>
                    <h4>{formatNumber(portfolioSummary?.total_expected_return || 0)} $</h4>
                    <p>Expected Return</p>
                  </div>
                  <div className='balance_vector'>
                    <img src="/images/wallet_coins_balance2.svg" alt="Earning Balance" />
                  </div>
                </li>
              </ul>

              <div className="wallet_balance_tb">
                <div className="user_list_top walletbalance_t">
                  <div className="user_list_l">
                    <h4>Earning Assets</h4>
                  </div>
                </div>

                <div className='table-responsive'>

                  {earningAssetsPortfolio?.length > 0 ? (
                    earningAssetsPortfolio.map((item, index) => (
                      <ul className='earning_assets_list' key={item._id || index}>
                        <li>
                          <span>
                            <img
                              src={ApiConfig?.baseImage + item?.icon_path}
                              alt={item?.currency}
                              width="20"
                              height="20"
                              style={{ marginRight: '8px', verticalAlign: 'middle' }}
                              onError={(e) => { e.target.src = '/images/default-coin.png'; }}
                            />
                            {item?.currency}
                          </span>
                          {item?.currency_fullname}
                        </li>
                        <li><span>Total Invested</span>{formatNumber(item?.invested_amount || 0)} {item?.currency}</li>
                        <li><span>Expected Return</span>{formatNumber(item?.expected_return || 0)} {item?.currency}</li>
                        <li><span>Running</span>{formatNumber(item?.running_investment || 0)} {item?.currency}</li>
                        {/* <li><span>Bonus Remaining</span>{formatNumber(item?.bonus_remaining || 0)} {item?.currency}</li> */}
                      </ul>
                    ))
                  ) : (
                    <>
                     
                      <div className="no-data-wrapper" style={{ padding: '40px 20px', textAlign: 'center' }}>
                        <div className="no_data_s">
                          <img src="/images/no_data_vector.svg" className="img-fluid dark_img" alt="" />
                          <img src="/images/no_data_vector_light.png" className="img-fluid light_img" alt=""/>
                          <small className=" mt-2">Start investing to see your portfolio</small>
                        </div>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>

              <div className="dashboard_recent_s productdata position_order">
                <div className='d-flex justify-content-between align-items-center'>
                  <h4>Earning Balance History</h4>
                  <div className="searchBar custom-tabs">
                    <i className="ri-search-2-line"></i>
                    <input
                      type="search"
                      className="custom_search"
                      placeholder="Search token"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>

                <div className="user_list_top rowtable">
                  <div className="user_list_l earning_section_cate responsive-table">
                    <ul className="position_list nav nav-tabs" id="earningHistoryTab" role="tablist">
                      <li className="nav-item" role="presentation">
                        <button
                          className="nav-link active"
                          id="active-tab"
                          data-bs-toggle="tab"
                          data-bs-target="#activeTab"
                          type="button"
                          role="tab"
                        >
                          Active
                        </button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button
                          className="nav-link"
                          id="completed-tab"
                          data-bs-toggle="tab"
                          data-bs-target="#completedTab"
                          type="button"
                          role="tab"
                        >
                          Completed
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className='table-responsive recenttable_s'>
                  <div className="tab-content" id="earningHistoryTabContent">
                    {/* Active Subscriptions */}
                    <div className="tab-pane fade show active" id="activeTab" role="tabpanel">

                      <div className='order_history_mobile_view'>
                        {filteredActivePackages?.length > 0 ? (
                          filteredActivePackages.map((item, index) => {
                            console.log("🚀 ~ Earning ~ item?.usdt_value:", item?.usdt_value)
                            return <div className='d-flex mb-3' key={item._id || index}>
                              <div className='order_datalist'>
                                <ul className='listdata'>
                                  <li>
                                    <span className='date'>{item?.currency}</span>
                                    <span className='date_light'>{moment(item.start_date).format("DD/MM/YYYY")}</span>
                                  </li>
                                  <li>
                                    <span>Subscription Amount</span>
                                    <span>{formatToNineDecimals(parseFloat(item?.invested_amount?.$numberDecimal || 0))} {item?.currency}</span>
                                  </li>
                                  <li>
                                    <span>Receivable Amount</span>
                                    <span>{formatToNineDecimals(parseFloat(item?.expected_return?.$numberDecimal || 0))} {item?.currency}</span>
                                  </li>
                                  <li>
                                    <span>Status</span>
                                    <span className='text-white '> <strong>{item?.status}</strong></span>
                                  </li>
                                  <li>
                                    <span>Payout history</span>
                                    <span className="d-inline-flex flex-column gap-2 align-items-stretch">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary px-3 py-1"
                                        onClick={() => openPayoutHistory(item)}
                                      >
                                        View
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger px-3 py-1"
                                        disabled={cancellingStakingId === item._id}
                                        onClick={() => handleCancelSubscription(item)}
                                      >
                                        {cancellingStakingId === item._id ? "…" : "Cancel"}
                                      </button>
                                    </span>
                                  </li>
                                  {showActiveExpandedRows[index] && (
                                    <>
                                      <li>
                                        <span>Deducted From</span>
                                        <span >{item?.wallet_type?.charAt(0).toUpperCase() + item?.wallet_type?.slice(1)} Wallet</span>
                                      </li>
                                      <li>
                                        <span>Duration</span>
                                        <span>{item?.duration_days} days</span>
                                      </li>
                                      <li>
                                        <span>Start Date</span>
                                        <span>{moment(item.start_date).format("YYYY-MM-DD")}</span>
                                      </li>
                                      <li>
                                        <span>Mature Date</span>
                                        <span>{moment(item.end_date).format("YYYY-MM-DD")}</span>
                                      </li>
                                      <li>
                                        <span>Bonus Amount</span>
                                        <span >+{formatToNineDecimals(parseFloat(item?.expected_return?.$numberDecimal || 0) - parseFloat(item?.invested_amount?.$numberDecimal || 0))}</span>
                                      </li>
                                      <li>
                                        <span>ZILN Price</span>
                                        <span>{item?.subscription_zil_price != null ? formatNumber(parseFloat(item.subscription_zil_price), 4) : "—"}</span>
                                      </li>
                                    </>
                                  )}
                                </ul>
                                <button
                                  type="button"
                                  className="view_more_btn"
                                  onClick={() => setShowActiveExpandedRows({ ...showActiveExpandedRows, [index]: !showActiveExpandedRows[index] })}
                                >
                                  {showActiveExpandedRows[index] ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-up-s-line"></i>}
                                </button>
                              </div>
                            </div>;
                          })
                        ) : (
                          <div className="no-data-wrapper">
                            <div className="no_data_s">
                              <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                              <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                            </div>
                            {!token && <p>Please <Link to='/login'>Login</Link> to continue</p>}
                          </div>
                        )}
                      </div>

                      <div className='desktop_view'>
                        <div className="table-responsive">
                        <table>
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th>Currency</th>
                              <th>Deducted From</th>
                              <th>Duration</th>
                              <th>Start Date</th>
                              <th>Mature Date</th>
                              <th>Subscription Amount</th>
                              <th>Bonus Amount</th>
                              <th>Receivable Amount</th>
                              <th>ZILN Price</th>
                              <th>USDT Value</th>
                              <th>Status</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredActivePackages?.length > 0 ? (
                              filteredActivePackages.map((item, index) => (
                                <tr key={item._id || index}>
                                  <td>{skipActive + index + 1}</td>
                                  <td>{item?.currency}</td>
                                  <td >
                                    {item?.wallet_type?.charAt(0).toUpperCase() + item?.wallet_type?.slice(1)} Wallet
                                  </td>
                                  <td>{item?.duration_days} days</td>
                                  <td>{moment(item.start_date).format("YYYY-MM-DD")}</td>
                                  <td>{moment(item.end_date).format("YYYY-MM-DD")}</td>
                                  <td>{formatToNineDecimals(parseFloat(item?.invested_amount?.$numberDecimal || 0))}</td>
                                  <td>
                                    +{formatToNineDecimals(parseFloat(item?.expected_return?.$numberDecimal || 0) - parseFloat(item?.invested_amount?.$numberDecimal || 0))}
                                  </td>
                                  <td>{formatToNineDecimals(parseFloat(item?.expected_return?.$numberDecimal || 0))}</td>
                                  <td>{item?.subscription_zil_price != null ? formatNumber(parseFloat(item.subscription_zil_price), 4) : "—"}</td>
                                  <td>{item?.usdt_value != null ? formatNumber(parseFloat(item.usdt_value?.$numberDecimal), 2) : "—"}</td>
                                  <td className='text-white'><strong>{item?.status}</strong> </td>
                                  <td className="text-end">
                                    <div className="d-inline-flex flex-column gap-2 align-items-stretch">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => openPayoutHistory(item)}
                                      >
                                        View
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        disabled={cancellingStakingId === item._id}
                                        onClick={() => handleCancelSubscription(item)}
                                      >
                                        {cancellingStakingId === item._id ? "…" : "Cancel"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr rowSpan="5" className="no-data-row">
                                <td colSpan="13">
                                  <div className="no-data-wrapper">
                                    <div className="no_data_s">
                                      <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                                      <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                                    </div>
                                    {!token && <p>Please <Link to='/login'>Login</Link> to continue</p>}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    </div>

                    {/* Completed Subscriptions */}
                    <div className="tab-pane fade" id="completedTab" role="tabpanel">

                      <div className='order_history_mobile_view'>
                        {filteredCompletedPackages?.length > 0 ? (
                          filteredCompletedPackages.map((item, index) => (
                            <div className='d-flex mb-3' key={item._id || index}>
                              <div className='order_datalist'>
                                <ul className='listdata'>
                                  <li>
                                    <span className='date'>{item?.currency}</span>
                                    <span className='date_light'>{moment(item.start_date).format("DD/MM/YYYY")}</span>
                                  </li>
                                  <li>
                                    <span>Subscription Amount</span>
                                    <span>{formatToNineDecimals(parseFloat(item?.invested_amount?.$numberDecimal || 0))} {item?.currency}</span>
                                  </li>
                                  <li>
                                    <span>Received Amount</span>
                                    <span className='text-success'>{formatToNineDecimals(parseFloat(item?.expected_return?.$numberDecimal || 0))} {item?.currency}</span>
                                  </li>
                                  <li>
                                    <span>Status</span>
                                    <span className='text-success'>{item?.status}</span>
                                  </li>
                                  <li>
                                    <span>Payout history</span>
                                    <span>
                                      <button
                                        type="button"
                                        className="btn btn-sm custom-btn px-3 py-1"
                                        onClick={() => openPayoutHistory(item)}
                                      >
                                        View
                                      </button>
                                    </span>
                                  </li>
                                  {showCompletedExpandedRows[index] && (
                                    <>
                                      <li>
                                        <span>Received In</span>
                                        <span className='text-success'>{item?.credited_wallet_type?.charAt(0).toUpperCase() + item?.credited_wallet_type?.slice(1)} Wallet</span>
                                      </li>
                                      <li>
                                        <span>Duration</span>
                                        <span>{item?.duration_days} days</span>
                                      </li>
                                      <li>
                                        <span>Start Date</span>
                                        <span>{moment(item.start_date).format("YYYY-MM-DD")}</span>
                                      </li>
                                      <li>
                                        <span>Mature Date</span>
                                        <span>{moment(item.end_date).format("YYYY-MM-DD")}</span>
                                      </li>
                                      <li>
                                        <span>Bonus</span>
                                        <span className='text-success'>+{formatToNineDecimals(parseFloat(item?.expected_return?.$numberDecimal || 0) - parseFloat(item?.invested_amount?.$numberDecimal || 0))}</span>
                                      </li>
                                      <li>
                                        <span>ZILN Price</span>
                                        <span>{item?.subscription_zil_price != null ? formatNumber(parseFloat(item.subscription_zil_price), 4) : "—"}</span>
                                      </li>
                                      <li>
                                        <span>USDT Value</span>
                                        <span>{item?.usdt_value != null ? formatNumber(parseFloat(item.usdt_value), 2) : "—"}</span>
                                      </li>
                                    </>
                                  )}
                                </ul>
                                <button
                                  type="button"
                                  className="view_more_btn"
                                  onClick={() => setShowCompletedExpandedRows({ ...showCompletedExpandedRows, [index]: !showCompletedExpandedRows[index] })}
                                >
                                  {showCompletedExpandedRows[index] ? <i className="ri-arrow-down-s-line"></i> : <i className="ri-arrow-up-s-line"></i>}
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="no-data-wrapper">
                            <div className="no_data_s">
                              <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                              <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                            </div>
                            {!token && <p>Please <Link to='/login'>Login</Link> to continue</p>}
                          </div>
                        )}
                      </div>

                      <div className='desktop_view'>
                      <div className="table-responsive">
                        <table>
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th>Currency</th>
                              <th>Received In</th>
                              <th>Duration</th>
                              <th>Start Date</th>
                              <th>Mature Date</th>
                              <th>Subscription Amount</th>
                              <th>Bonus</th>
                              <th>Received Amount</th>
                              <th>ZILN Price</th>
                              <th>USDT Value</th>
                              <th>Status</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCompletedPackages?.length > 0 ? (
                              filteredCompletedPackages.map((item, index) => (
                                <tr key={item._id || index}>
                                  <td>{skipCompleted + index + 1}</td>
                                  <td>{item?.currency}</td>
                                  <td className='text-success'>
                                    {item?.credited_wallet_type?.charAt(0).toUpperCase() + item?.credited_wallet_type?.slice(1)} Wallet
                                  </td>
                                  <td>{item?.duration_days} days</td>
                                  <td>{moment(item.start_date).format("YYYY-MM-DD")}</td>
                                  <td>{moment(item.end_date).format("YYYY-MM-DD")}</td>
                                  <td>{formatToNineDecimals(parseFloat(item?.invested_amount?.$numberDecimal || 0))}</td>
                                  <td className='text-success'>
                                    +{formatToNineDecimals(parseFloat(item?.expected_return?.$numberDecimal || 0) - parseFloat(item?.invested_amount?.$numberDecimal || 0))}
                                  </td>
                                  <td className='text-success'>
                                    {formatToNineDecimals(parseFloat(item?.expected_return?.$numberDecimal || 0))}
                                  </td>
                                  <td>{item?.subscription_zil_price != null ? formatNumber(parseFloat(item.subscription_zil_price), 4) : "—"}</td>
                                  <td>{item?.usdt_value != null ? formatNumber(parseFloat(item.usdt_value), 2) : "—"}</td>
                                  <td className='text-success'>{item?.status}</td>
                                  <td className="text-end">
                                    <button
                                      type="button"
                                      className="btn btn-sm custom-btn"
                                      onClick={() => openPayoutHistory(item)}
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr rowSpan="5" className="no-data-row">
                                <td colSpan="13">
                                  <div className="no-data-wrapper">
                                    <div className="no_data_s">
                                      <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                                      <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                                    </div>
                                    {!token && <p>Please <Link to='/login'>Login</Link> to continue</p>}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* Check Level Performance Tab */}
            <div className="tab-pane fade" id="levelPerformance" role="tabpanel" aria-labelledby="level-performance-tab">
              <div className='exchange_earning_bnr'>
                <div className='earningbnr_cnt'>
                  <h2>Staking Team Level Performance</h2>
                  <p>Track your team's staking performance across every level.</p>
                  {!token && (
                    <button className='signbtn' onClick={() => navigate('/register')}>Sign Up Now</button>
                  )}
                </div>
                <div className='earning_bnr'>
                  <img src="/images/earning_balance_vector.png" alt="Team Level Performance" />
                </div>
              </div>

              <ul className='balance_list_s'>
                <li>
                  <div className='balance_cnt'>
                    <h4>{teamPerformanceSummary?.total_team_members || 0}</h4>
                    <p>Total Team Members</p>
                  </div>
                  <div className='balance_vector'>
                    <img src="/images/wallet_coins_balance.svg" alt="Total Team Members" />
                  </div>
                </li>
                <li>
                  <div className='balance_cnt'>
                    <h4>{teamPerformanceSummary?.members_with_staking || 0}</h4>
                    <p>Members With Staking</p>
                  </div>
                  <div className='balance_vector'>
                    <img src="/images/wallet_coins_balance2.svg" alt="Members With Staking" />
                  </div>
                </li>
                <li>
                  <div className='balance_cnt'>
                    <h4>{formatNumber(teamPerformanceSummary?.grand_total_staked_usdt || 0)} $</h4>
                    <p>Total Staked (USDT)</p>
                  </div>
                  <div className='balance_vector'>
                    <img src="/images/wallet_coins_balance3.svg" alt="Total Staked" />
                  </div>
                </li>
                <li>
                  <div className='balance_cnt'>
                    <h4 className='text-green'>{formatNumber(teamPerformanceSummary?.grand_total_active_usdt || 0)} $</h4>
                    <p>Active Staked (USDT)</p>
                  </div>
                  <div className='balance_vector'>
                    <img src="/images/wallet_coins_balance.svg" alt="Active Staked" />
                  </div>
                </li>
                <li>
                  <div className='balance_cnt'>
                    <h4>{formatNumber(teamPerformanceSummary?.grand_total_completed_usdt || 0)} $</h4>
                    <p>Completed Staked (USDT)</p>
                  </div>
                  <div className='balance_vector'>
                    <img src="/images/wallet_coins_balance2.svg" alt="Completed Staked" />
                  </div>
                </li>
              </ul>

              {/* Level-wise breakdown */}
              {teamPerformanceSummary?.by_level && Object.keys(teamPerformanceSummary.by_level).length > 0 && (
                <div className="wallet_balance_tb">
                  <div className="user_list_top walletbalance_t">
                    <div className="user_list_l">
                      <h4>Level-wise Summary</h4>
                    </div>
                  </div>
                  <div className='table-responsive'>
                    {Object.entries(teamPerformanceSummary.by_level)
                      .sort((a, b) => Number(a[0]) - Number(b[0]))
                      .map(([level, data]) => (
                        <ul className='earning_assets_list' key={level}>
                          <li><span>Level</span>Level {level}</li>
                          <li><span>Members</span>{data?.members || 0}</li>
                          <li><span>Total Staked</span>{formatNumber(data?.total_staked_usdt || 0)} $</li>
                        </ul>
                      ))}
                  </div>
                </div>
              )}

              {/* Members performance list */}
              <div className="dashboard_recent_s productdata position_order">
                <div className='d-flex justify-content-between align-items-center'>
                  <h4>Team Members Performance</h4>
                </div>

                <div className='table-responsive recenttable_s'>
                  {/* Desktop view */}
                  <div className='desktop_view'>
                    <div className="table-responsive">
                      <table>
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Name</th>
                            <th>Referral Code</th>
                            <th>Level</th>
                            <th>Total Staked (USDT)</th>
                            <th>Active (USDT)</th>
                            <th>Completed (USDT)</th>
                            <th>Plans</th>
                            <th>Active</th>
                            <th>Completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamPerformanceMembers?.length > 0 ? (
                            teamPerformanceMembers.map((member, index) => (
                              <tr key={member.user_id || index}>
                                <td>{index + 1}</td>
                                <td>{member?.name || "—"}</td>
                                <td>{member?.referral_code || "—"}</td>
                                <td>Level {member?.level}</td>
                                <td>{formatNumber(member?.total_staked_usdt || 0)}</td>
                                <td className='text-green'>{formatNumber(member?.active_staked_usdt || 0)}</td>
                                <td>{formatNumber(member?.completed_staked_usdt || 0)}</td>
                                <td>{member?.plans_count || 0}</td>
                                <td>{member?.active_count || 0}</td>
                                <td>{member?.completed_count || 0}</td>
                              </tr>
                            ))
                          ) : (
                            <tr rowSpan="5" className="no-data-row">
                              <td colSpan="10">
                                <div className="no-data-wrapper">
                                  <div className="no_data_s">
                                    <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                                    <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                                  </div>
                                  {!token && <p>Please <Link to='/login'>Login</Link> to continue</p>}
                                  {token && !teamPerformanceLoading && <p>No team staking data found</p>}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile view */}
                  <div className='order_history_mobile_view'>
                    {teamPerformanceMembers?.length > 0 ? (
                      teamPerformanceMembers.map((member, index) => (
                        <div className='d-flex mb-3' key={member.user_id || index}>
                          <div className='order_datalist'>
                            <ul className='listdata'>
                              <li>
                                <span className='date'>{member?.name || "—"}</span>
                                <span className='date_light'>Level {member?.level}</span>
                              </li>
                              <li>
                                <span>Referral Code</span>
                                <span>{member?.referral_code || "—"}</span>
                              </li>
                              <li>
                                <span>Total Staked (USDT)</span>
                                <span>{formatNumber(member?.total_staked_usdt || 0)}</span>
                              </li>
                              <li>
                                <span>Active (USDT)</span>
                                <span className='text-green'>{formatNumber(member?.active_staked_usdt || 0)}</span>
                              </li>
                              <li>
                                <span>Completed (USDT)</span>
                                <span>{formatNumber(member?.completed_staked_usdt || 0)}</span>
                              </li>
                              <li>
                                <span>Plans / Active / Completed</span>
                                <span>{member?.plans_count || 0} / {member?.active_count || 0} / {member?.completed_count || 0}</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-data-wrapper">
                        <div className="no_data_s">
                          <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                          <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                        </div>
                        {!token && <p>Please <Link to='/login'>Login</Link> to continue</p>}
                        {token && !teamPerformanceLoading && <p>No team staking data found</p>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Referral Performance Team Tab */}
            <div className="tab-pane fade" id="referralPerformance" role="tabpanel" aria-labelledby="referral-performance-tab">
              <ReferralHistorySection showHowItWorks={false} />
            </div>

            {/* Brokerage Commission Tab */}
            <div className="tab-pane fade" id="brokerageCommission" role="tabpanel" aria-labelledby="brokerage-commission-tab">
              <div className='exchange_earning_bnr'>
                <div className='earningbnr_cnt'>
                  <h2>Brokerage Commission</h2>
                  <p>See how much trade commission you earned, from whom and when.</p>
                  {!token && (
                    <button className='signbtn' onClick={() => navigate('/register')}>Sign Up Now</button>
                  )}
                </div>
                <div className='earning_bnr'>
                  <img src="/images/earning_balance_vector.png" alt="Brokerage Commission" />
                </div>
              </div>

              <ul className='balance_list_s'>
                <li>
                  <div className='balance_cnt'>
                    <h4 className='text-green'>{formatToNineDecimals(parseFloat(brokerageSummary?.total_received || 0))} $</h4>
                    <p>Total Commission Received</p>
                  </div>
                  <div className='balance_vector'>
                    <img src="/images/wallet_coins_balance.svg" alt="Total Commission" />
                  </div>
                </li>
                <li>
                  <div className='balance_cnt'>
                    <h4>{brokerageList?.length || 0}</h4>
                    <p>Total Commission Entries</p>
                  </div>
                  <div className='balance_vector'>
                    <img src="/images/wallet_coins_balance2.svg" alt="Total Entries" />
                  </div>
                </li>
              </ul>

              {/* Level-wise breakdown */}
              {brokerageSummary?.by_level && Object.keys(brokerageSummary.by_level).length > 0 && (
                <div className="wallet_balance_tb">
                  <div className="user_list_top walletbalance_t">
                    <div className="user_list_l">
                      <h4>Level-wise Commission</h4>
                    </div>
                  </div>
                  <div className='table-responsive'>
                    {Object.entries(brokerageSummary.by_level)
                      .sort((a, b) => Number(a[0]) - Number(b[0]))
                      .map(([level, amount]) => (
                        <ul className='earning_assets_list' key={level}>
                          <li><span>Level</span>Level {level}</li>
                          <li><span>Entries</span>{brokerageSummary?.by_level_count?.[level] || 0}</li>
                          <li><span>Commission</span>{formatNumber(amount || 0)} $</li>
                        </ul>
                      ))}
                  </div>
                </div>
              )}

              {/* Commission list */}
              <div className="dashboard_recent_s productdata position_order">
                <div className='d-flex justify-content-between align-items-center'>
                  <h4>Commission History</h4>
                </div>

                <div className='table-responsive recenttable_s'>
                  {/* Desktop view */}
                  <div className='desktop_view'>
                    <div className="table-responsive">
                      <table>
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>From User</th>
                            <th>Level</th>
                            <th>Currency</th>
                            <th>Trade Fee</th>
                            <th>Commission %</th>
                            <th>Commission Amount</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {brokerageList?.length > 0 ? (
                            brokerageList.map((item, index) => (
                              <tr key={item._id || index}>
                                <td>{index + 1}</td>
                                <td>{item?.from_user?.name || item?.from_user?.uuid || "—"}</td>
                                <td>Level {item?.level}</td>
                                <td>{item?.currency || "—"}</td>
                                <td>{formatNumber(item?.trade_fee || 0)}</td>
                                <td>{formatNumber(item?.commission_percentage || 0)}%</td>
                                <td className='text-green'>+{formatToNineDecimals(parseFloat(item?.credited_amount || 0))} {item?.credited_currency || "—"}</td>
                                <td>{item?.created_at ? moment(item.created_at).format("DD/MM/YYYY hh:mm A") : "—"}</td>
                              </tr>
                            ))
                          ) : (
                            <tr rowSpan="5" className="no-data-row">
                              <td colSpan="8">
                                <div className="no-data-wrapper">
                                  <div className="no_data_s">
                                    <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                                    <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                                  </div>
                                  {!token && <p>Please <Link to='/login'>Login</Link> to continue</p>}
                                  {token && !brokerageLoading && <p>No brokerage commission found</p>}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile view */}
                  <div className='order_history_mobile_view'>
                    {brokerageList?.length > 0 ? (
                      brokerageList.map((item, index) => (
                        <div className='d-flex mb-3' key={item._id || index}>
                          <div className='order_datalist'>
                            <ul className='listdata'>
                              <li>
                                <span className='date'>{item?.from_user?.name || item?.from_user?.uuid || "—"}</span>
                                <span className='date_light'>Level {item?.level}</span>
                              </li>
                              <li>
                                <span>Currency</span>
                                <span>{item?.currency || "—"}</span>
                              </li>
                              <li>
                                <span>Trade Fee</span>
                                <span>{formatNumber(item?.trade_fee || 0)}</span>
                              </li>
                              <li>
                                <span>Commission %</span>
                                <span>{formatNumber(item?.commission_percentage || 0)}%</span>
                              </li>
                              <li>
                                <span>Commission Amount</span>
                                <span className='text-green'>+{formatToNineDecimals(parseFloat(item?.credited_amount || 0))} {item?.credited_currency || "—"}</span>
                              </li>
                              <li>
                                <span>Date</span>
                                <span>{item?.created_at ? moment(item.created_at).format("DD/MM/YYYY hh:mm A") : "—"}</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-data-wrapper">
                        <div className="no_data_s">
                          <img src="/images/no_data_vector.svg" className="dark_img" alt="no-data" />
                          <img src="/images/no_data_vector_light.png" className="light_img" alt="no-data"/>
                        </div>
                        {!token && <p>Please <Link to='/login'>Login</Link> to continue</p>}
                        {token && !brokerageLoading && <p>No brokerage commission found</p>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="tab-pane fade" id="myReferrals" role="tabpanel" aria-labelledby="my-referrals-tab">
              <ReferralDetailsSection />
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* <!-- Modal earning popup start --> */}
      <div
        className="modal fade earningpopup"
        id="package_details"
        tabIndex="-1"
        aria-labelledby="exampleModalLabel"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetInput}
              ></button>
            </div>

            <div className="modal-body text-center">
              <div className='earining_popup_exchange'>
                <div className='lft_pop_cnt'>
                  <h3>
                    <img
                      src={packageDetails?.icon_path ? ApiConfig?.baseImage + packageDetails?.icon_path : "/images/tether_icon.png"}
                      alt={packageDetails?.currency || "coin"}
                      onError={(e) => { e.target.src = '/images/tether_icon.png'; }}
                    />
                    {packageDetails?.currency || "N/A"}
                  </h3>

                  <ul className='daylist'>
                    {selectedCurrencyPackages.length > 0 ? (
                      selectedCurrencyPackages.map((pkg) => (
                        <li
                          key={pkg._id}
                          className={packageDetails?._id === pkg._id ? 'active' : ''}
                          onClick={() => handleDurationSelect(pkg)}
                          style={{ cursor: 'pointer' }}
                        >
                          <span>{pkg.duration_days} Day</span>
                          {pkg.return_percentage?.toFixed(2)}%
                        </li>
                      ))
                    ) : (
                      <li className='active'>
                        <span>{packageDetails?.duration_days || 0} Day</span>
                        {packageDetails?.return_percentage?.toFixed(2) || "0.00"}%
                      </li>
                    )}
                  </ul>

                  <ul className='termslist'>
                    <li>Reference <span className='text-green'>{packageDetails?.return_percentage?.toFixed(2) || "0.00"}%</span></li>
                    <li>Term<span><strong>{packageDetails?.duration_days || 0} days</strong></span></li>
                    <li>Return ({packageDetails?.return_type ? `${packageDetails.return_type}` : ""})<span><strong>{packageDetails?.returnPercenatgeShowtoUser ?? "—"}</strong>%{packageDetails?.return_type ? ` ${packageDetails.return_type}` : ""}</span></li>
                  </ul>

                  <div className='payment_method_f'>

                    <div className='payment_inquery'>
                      <h3>Payment Method</h3>
                      <div className='select_option'>
                        <select
                          value={selectedWallet}
                          onChange={(e) => setSelectedWallet(e.target.value)}
                        >
                          {walletType.map((wallet, index) => (
                            <option key={index} value={wallet}>
                              {getSubscribeWalletLabel(wallet)}
                            </option>
                          ))}
                        </select>
                      </div>
                      {isStakingWallet(selectedWallet) && (
                        <p className="staking-wallet-hint">{STAKING_WALLET_COPY.subscribeSubtitle}</p>
                      )}
                    </div>

                    <div className='payment_inquery'>
                      <h3>Subscription Amount </h3>
                      <div className='amount_input'>
                        <input
                          type='text'
                          placeholder={`Enter Subscription Amount (Min ${packageDetails?.min_amount} ${packageDetails?.currency})`}
                          value={subscriptionAmount}
                          onChange={handleSubscriptionAmountChange}
                        />
                        <span className='max text-green' onClick={handleMaxAmount} style={{ cursor: 'pointer' }}>Max</span>
                      </div>
                    </div>

                  </div>

                  <ul className='termslist border-0'>
                    <li>
                      Funding Account
                      <span>
                        {isStakingWallet(selectedWallet) && Number(walletBalance) <= 0
                          ? STAKING_WALLET_COPY.emptyBalance
                          : `${walletBalance} ${packageDetails?.currency || ""}`}
                        <i className="ri-add-circle-line"></i>
                      </span>
                    </li>
                    <li>Max Account<span className='text-light'>{packageDetails?.max_amount || "Unlimited"} {packageDetails?.currency}</span></li>
                  </ul>

                </div>

                <div className='right_cnt_pop'>
                  <h4>Preview</h4>

                  <ul className='subscriptionlist'>
                    <li>Subscription Date<span>{moment().format("D/M/YYYY, HH:mm")}</span></li>
                    <li>Accrual Date <span>{moment().add(1, 'days').format("D/M/YYYY, HH:mm")}</span></li>
                    <li>Profit Distribution Date <span>{moment().add(packageDetails?.duration_days || 0, 'days').format("D/M/YYYY, 17:30")}</span></li>
                    <li>Date of Maturity <span>{moment().add(packageDetails?.duration_days || 0, 'days').format("D/M/YYYY")}</span></li>
                  </ul>

                  <ul className='termslist border-0'>
                    <li>Redemption Period <span>{packageDetails?.duration_days || 0} days</span></li>
                    <li>Profit Received<span>At Maturity</span></li>
                  </ul>

                  <div className='estimated'>
                    <h4>Estimated Returns</h4>

                    <ul className='termslist border-0 mb-0'>
                      <li>Daily Earnings <span>{dailyReturns} {packageDetails?.currency} / D</span></li>
                      <li>Total Earnings ({packageDetails?.duration_days || 0}D) <span className='text-green'>+{totalReturns} {packageDetails?.currency}</span></li>
                      <li>Total Receivable <span className='text-green'>{totalReceivable} {packageDetails?.currency}</span></li>
                    </ul>

                    <ul className='estimatedlist'>
                      <li>* At maturity, your funds are seamlessly transferred to you earning balance.</li>
                      <li>* Early withdrawals are not permitted. In case of cancellation before maturity, profits will not be applicable.</li>
                    </ul>
                    <label>
                      <input
                        type='checkbox'
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                      /> I have read and agree to the <a href='/terms'>Earn Service Agreement.</a>
                    </label>
                    <button
                      className='subscribebtn'
                      onClick={subscribeEarningPackage}
                      disabled={isStakingDisabled || isSubscribing || !agreeTerms || !subscriptionAmount || parseFloat(subscriptionAmount) <= 0 || parseFloat(subscriptionAmount) > Number(walletBalance)}
                    >
                      {isSubscribing ? 'Processing...' : isStakingDisabled ? 'Staking Disabled' : 'Subscription'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Per-day payout history (staking subscription) — premium modal */}
      <div
        className="modal fade eph-payout-modal"
        id="payout_history_modal"
        tabIndex="-1"
        aria-labelledby="payoutHistoryModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable eph-payout-modal__dialog">
          <div className="modal-content eph-payout-modal__sheet">
            <div className="eph-payout-modal__head">
              <div className="eph-payout-modal__head-main">
                <div className="eph-payout-modal__icon-wrap" aria-hidden>
                  <i className="ri-line-chart-line" />
                </div>
                <div>
                  <h2 className="eph-payout-modal__title" id="payoutHistoryModalLabel">
                    Daily payout history
                  </h2>
                  <p className="eph-payout-modal__subtitle">
                    Day-by-day payouts credited for this subscription. Dates are shown in your local time.
                  </p>
                </div>
              </div>
              <div className="eph-payout-modal__head-actions">
                {payoutModalStaking?.currency ? (
                  <span className="eph-payout-modal__badge">{payoutModalStaking.currency}</span>
                ) : null}
                <button
                  type="button"
                  className="btn-close eph-payout-modal__close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>
            </div>
            <div className="modal-body eph-payout-modal__body">
              {!payoutHistoryLoading && !payoutHistoryError && payoutHistoryList.length > 0 ? (
                <div className="eph-payout-modal__meta">
                  <span className="eph-payout-modal__meta-pill">
                    <strong>{payoutHistoryList.length}</strong>
                    &nbsp;payout{payoutHistoryList.length !== 1 ? "s" : ""} recorded
                  </span>
                </div>
              ) : null}
              {payoutHistoryLoading ? (
                <div className="eph-payout-modal__loading">
                  <div className="eph-payout-modal__spinner" aria-hidden />
                  <span>Loading payout history…</span>
                </div>
              ) : payoutHistoryError ? (
                <div className="eph-payout-modal__alert" role="alert">
                  {payoutHistoryError}
                </div>
              ) : payoutHistoryList.length === 0 ? (
                <div className="eph-payout-modal__empty">
                  <div className="eph-payout-modal__empty-icon" aria-hidden>
                    <i className="ri-file-list-3-line" />
                  </div>
                  <h4>No payouts yet</h4>
                  <p>
                    Daily rewards will appear here after each payout is processed for this plan.
                  </p>
                </div>
              ) : (
                <div className="eph-payout-modal__table-shell">
                  <div className="eph-payout-modal__table-scroll">
                    <table className="eph-payout-modal__table">
                      <thead>
                        <tr>
                          <th>Sr. No.</th>
                          <th>Amount</th>
                          <th>Paid at</th>
                          {/* <th>Payout Date</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {payoutHistoryList.map((row) => (
                          <tr key={row._id || `payout-day-${row.day}`}>
                            <td>
                              <span className="eph-payout-modal__day">{row.day}</span>
                            </td>
                            <td>
                              <span className="eph-payout-modal__amount">
                                {formatToNineDecimals(parsePayoutAmount(row.amount))}
                              </span>
                              <span className="eph-payout-modal__amount-sym">
                                {payoutModalStaking?.currency || ""}
                              </span>
                            </td>
                            <td>
                              <span
                                className={
                                  row.paid_at
                                    ? "eph-payout-modal__datetime"
                                    : "eph-payout-modal__datetime eph-payout-modal__datetime--muted"
                                }
                              >
                                {row.paid_at
                                  ? moment(row.paid_at).format("MMM D, YYYY · HH:mm")
                                  : "—"}
                              </span>
                            </td>
                            {/* <td>
                              <span
                                className={
                                  row.next_payout_date
                                    ? "eph-payout-modal__datetime"
                                    : "eph-payout-modal__datetime eph-payout-modal__datetime--muted"
                                }
                              >
                                {row.next_payout_date
                                  ? moment(row.next_payout_date).format("MMM D, YYYY · HH:mm")
                                  : "—"}
                              </span>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* <!-- Modal   more details Pop Up End --> */}
    </>
  );
}

export default Earning;
